import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Bell, Crosshair, MagnifyingGlass, MapPin, SlidersHorizontal } from 'phosphor-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useActiveCenter } from '@/hooks/useActiveCenter';
import { upperCity } from '@/lib/city';
import { Chip } from '@/components/ui/Chip';
import { GoldenRule } from '@/components/ui/GoldenRule';
import { EventCard } from '@/components/EventCard';
import { EventDetailSheet } from '@/components/EventDetailSheet';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { CATEGORY_LIST, DISTANCE_CHIPS, TIME_CHIPS, type TimeChipId, type DistanceFilter } from '@/constants/categories';
import { getSurtitre } from '@/lib/dynamicTitle';
import type { EventSearchRow } from '@/types/database';
import { Button } from '@/components/ui/Button';
import { addDays, endOfDay, endOfMonth, endOfWeek, format, startOfDay } from 'date-fns';
import { EditorialDateRangePicker } from '@/components/ui/EditorialDateRangePicker';
import { formatRangeChip, formatRangeProse } from '@/lib/dates';
import { collectKinds, cycleEventKindFilter, type EventKindFilters } from '@/lib/eventKindFilters';
import { writeGuestVacationLocation } from '@/lib/guestLocation';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { Input } from '@/components/ui/Input';
import { AdNativeCard } from '@/components/ads/AdNativeCard';
import { interleaveAds, isAdMarker, AD_FREQUENCY } from '@/lib/ads';

function rangeFor(c: TimeChipId, selectedFrom: string, selectedTo: string): { from: string; to: string } {
  const now = new Date();
  if (c === 'now') return { from: now.toISOString(), to: addDays(now, 1).toISOString() };
  if (c === 'tonight') { const e = new Date(now); e.setHours(23, 59, 59); return { from: now.toISOString(), to: e.toISOString() }; }
  if (c === 'tomorrow') { const t = addDays(startOfDay(now), 1); return { from: t.toISOString(), to: endOfDay(t).toISOString() }; }
  if (c === 'weekend') { const sat = addDays(startOfDay(now), (6 - now.getDay() + 7) % 7); return { from: sat.toISOString(), to: endOfWeek(sat, { weekStartsOn: 1 }).toISOString() }; }
  if (c === 'month') return { from: now.toISOString(), to: endOfMonth(now).toISOString() };
  if (c === 'year') return { from: now.toISOString(), to: addDays(now, 365).toISOString() };
  if (c === 'range') {
    const from = startOfDay(new Date(`${selectedFrom}T12:00:00`));
    const to = endOfDay(new Date(`${selectedTo}T12:00:00`));
    return { from: from.toISOString(), to: to.toISOString() };
  }
  return { from: now.toISOString(), to: addDays(now, 7).toISOString() };
}

export default function Discover() {
  const { profile, user, refreshProfile } = useAuth();
  const center = useActiveCenter();
  const [time, setTime] = useState<TimeChipId>(() => (sessionStorage.getItem('discover:time') as TimeChipId) ?? 'weekend');
  const [kindFilters, setKindFilters] = useState<EventKindFilters>(() => { try { return JSON.parse(sessionStorage.getItem('discover:kindFilters') ?? '{}'); } catch { return {}; } });
  const [radiusKm, setRadiusKm] = useState<DistanceFilter>(() => { const v = sessionStorage.getItem('discover:radiusKm'); return v ? (v === 'all' ? 'all' : Number(v) as DistanceFilter) : 50; });
  const [selectedFrom, setSelectedFrom] = useState(() => sessionStorage.getItem('discover:selectedFrom') ?? format(new Date(), 'yyyy-MM-dd'));
  const [selectedTo, setSelectedTo] = useState(() => sessionStorage.getItem('discover:selectedTo') ?? format(new Date(), 'yyyy-MM-dd'));
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const [sheetView, setSheetView] = useState<'filters' | 'city'>('filters');
  const [cityQ, setCityQ] = useState('');
  const [cityList, setCityList] = useState<{ display_name: string; lat: string; lon: string }[]>([]);
  const [cityBusy, setCityBusy] = useState(false);
  const [cityError, setCityError] = useState<string | null>(null);
  const [events, setEvents] = useState<EventSearchRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [blockingLoading, setBlockingLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const surtitre = useMemo(() => getSurtitre(new Date()), []);

  const hasGuestVacationLocation = center.mode === 'vacation' && !profile;
  const cityLabel = profile?.vacation_active
    ? profile.vacation_label
    : profile?.home_city ?? (hasGuestVacationLocation ? center.label : null);
  const includedKinds = useMemo(() => collectKinds(kindFilters, 'include'), [kindFilters]);
  const excludedKinds = useMemo(() => collectKinds(kindFilters, 'exclude'), [kindFilters]);
  const excludedKindsSet = useMemo(() => new Set(excludedKinds), [excludedKinds]);

  // Persistance des filtres sur navigation
  useEffect(() => {
    sessionStorage.setItem('discover:time', time);
    sessionStorage.setItem('discover:kindFilters', JSON.stringify(kindFilters));
    sessionStorage.setItem('discover:radiusKm', String(radiusKm));
    sessionStorage.setItem('discover:selectedFrom', selectedFrom);
    sessionStorage.setItem('discover:selectedTo', selectedTo);
  }, [time, kindFilters, radiusKm, selectedFrom, selectedTo]);

  async function searchCity() {
    if (!cityQ.trim()) return;
    setCityBusy(true);
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&countrycodes=fr&limit=5&q=${encodeURIComponent(cityQ)}`, { headers: { 'Accept-Language': 'fr' } });
      const d = await r.json();
      setCityList(d);
    } finally { setCityBusy(false); }
  }

  async function pickCity(c: { display_name: string; lat: string; lon: string }) {
    const loc = { active: true, label: c.display_name.split(',')[0], lat: parseFloat(c.lat), lon: parseFloat(c.lon) };
    setCityError(null);
    if (!user) {
      writeGuestVacationLocation(loc);
    } else {
      const { error } = await supabase.from('profiles').update({ vacation_active: true, vacation_label: loc.label, vacation_lat: loc.lat, vacation_lon: loc.lon }).eq('id', user.id);
      // Sans ce garde-fou, un échec (session expirée, réseau) était silencieux :
      // la vue se refermait et l'écran restait figé sur l'ancien lieu.
      if (error) { setCityError("Impossible d'enregistrer ce lieu. Vérifiez votre connexion et réessayez."); return; }
      await refreshProfile();
    }
    setCityQ(''); setCityList([]); setSheetView('filters');
  }

  async function useCurrentPosition() {
    setCityBusy(true);
    try {
      let lat: number; let lon: number;
      if (Capacitor.isNativePlatform()) {
        await Geolocation.requestPermissions();
        const pos = await Geolocation.getCurrentPosition({ timeout: 5000, maximumAge: 300_000, enableHighAccuracy: false });
        lat = pos.coords.latitude; lon = pos.coords.longitude;
      } else {
        const pos = await new Promise<GeolocationPosition>((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000, maximumAge: 300_000 })
        );
        lat = pos.coords.latitude; lon = pos.coords.longitude;
      }
      const loc = { active: true, label: 'Ma position', lat, lon };
      setCityError(null);
      if (!user) {
        writeGuestVacationLocation(loc);
      } else {
        const { error } = await supabase.from('profiles').update({ vacation_active: true, vacation_label: loc.label, vacation_lat: loc.lat, vacation_lon: loc.lon }).eq('id', user.id);
        if (error) { setCityBusy(false); setCityError("Impossible d'enregistrer ce lieu. Vérifiez votre connexion et réessayez."); return; }
        await refreshProfile();
      }
      setCityBusy(false); setSheetView('filters');
    } catch { setCityBusy(false); }
  }

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      setFetchError(false);
      const { from, to } = rangeFor(time, selectedFrom, selectedTo);
      const params = {
        p_lat: radiusKm === 'all' ? null : center.lat,
        p_lon: radiusKm === 'all' ? null : center.lon,
        p_radius_km: radiusKm === 'all' ? null : radiusKm,
        p_kinds: includedKinds.length ? includedKinds : null,
        p_audiences: null,
        p_from: from,
        p_to: to,
        p_limit: 200,
      };
      let lastError: unknown = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        const { data, error } = await supabase.rpc('search_events', params);
        if (!alive) return;
        if (!error) {
          const rows = (data as EventSearchRow[]) ?? [];
          setEvents(excludedKinds.length ? rows.filter((row) => !excludedKindsSet.has(row.kind)) : rows);
          setLoading(false);
          setBlockingLoading(false);
          return;
        }
        lastError = error;
        if (attempt < 2) await new Promise(r => setTimeout(r, 800 * (attempt + 1)));
        if (!alive) return;
      }
      console.error(lastError);
      setFetchError(true);
      setLoading(false);
      setBlockingLoading(false);
    }
    load();
    return () => { alive = false; };
  }, [center.lat, center.lon, excludedKinds.length, excludedKindsSet, includedKinds, radiusKm, selectedFrom, selectedTo, time, retryKey]);

  const hero = events[0];
  const grid = events.slice(1, 5);
  const numbered = events.slice(5, 17);
  const tail = events.slice(17);
  const rangeChipLabel = formatRangeChip(`${selectedFrom}T12:00:00`, `${selectedTo}T12:00:00`);
  const rangeProse = formatRangeProse(`${selectedFrom}T12:00:00`, `${selectedTo}T12:00:00`);

  // Labels pour les filtres
  const timeLabel = time === 'range' ? rangeChipLabel : (TIME_CHIPS.find(c => c.id === time)?.label ?? time);
  const timeLabelLower = timeLabel.charAt(0).toLowerCase() + timeLabel.slice(1);
  const activeFilterCount = [
    time !== 'weekend',
    radiusKm !== 50,
    Object.keys(kindFilters).length > 0,
  ].filter(Boolean).length;

  return (
    <div className="bg-paper text-ink-1 min-h-screen">
      {blockingLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-paper/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4 rounded-xl bg-paper-elev px-8 py-7 ring-1 ring-paper-edge shadow-sheet">
            <span className="h-10 w-10 animate-spin rounded-full border-[3px] border-paper-edge border-t-terre" aria-hidden="true" />
            <span className="t-ui-sm text-ink-2">Chargement des sorties…</span>
          </div>
        </div>
      )}
      {/* Top header */}
      <header className="px-5 pt-[max(env(safe-area-inset-top),12px)] pb-3 flex items-center gap-3">
        <Link to="/vacation" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-paper-elev ring-1 ring-paper-edge text-[13px]">
          <MapPin size={14} /> {cityLabel ?? 'Choisir un lieu'}
        </Link>
        <div className="ml-auto flex items-center gap-1.5">
          <Link to="/recherche" className="h-9 w-9 rounded-full grid place-items-center bg-paper-elev ring-1 ring-paper-edge"><MagnifyingGlass size={16} /></Link>
          <Link to="/alerts" className="h-9 w-9 rounded-full grid place-items-center bg-paper-elev ring-1 ring-paper-edge"><Bell size={16} /></Link>
        </div>
      </header>

      <div className="px-5 mb-5">
        <div className="t-cap mb-2">{profile ? surtitre.eyebrow : 'En ce moment'}</div>
        <h1 className="t-ed-3xl">
          {time === 'range' ? (
            <>
              Que faire{' '}
              <button onClick={() => setIsFilterSheetOpen(true)} className="text-terre bg-transparent border-0 p-0 font-[inherit] text-[inherit] leading-[inherit] cursor-pointer">
                {rangeProse}
              </button>
            </>
          ) : (
            <>
              Que faire{' '}
              <button onClick={() => setIsFilterSheetOpen(true)} className="text-terre bg-transparent border-0 p-0 font-[inherit] text-[inherit] leading-[inherit] cursor-pointer">
                {timeLabelLower}
              </button>
            </>
          )}
          {radiusKm === 'all' ? (
            <>{' '}dans{' '}
              <button onClick={() => setIsFilterSheetOpen(true)} className="text-terre bg-transparent border-0 p-0 font-[inherit] text-[inherit] leading-[inherit] cursor-pointer">toute la France</button>
            </>
          ) : (
            <>
              {' '}à{' '}
              <button onClick={() => setIsFilterSheetOpen(true)} className="text-terre bg-transparent border-0 p-0 font-[inherit] text-[inherit] leading-[inherit] cursor-pointer">
                {radiusKm} km
              </button>
              {cityLabel && (
                <>{' '}autour de{' '}
                  <button onClick={() => setIsFilterSheetOpen(true)} className="text-terre bg-transparent border-0 p-0 font-[inherit] text-[inherit] leading-[inherit] cursor-pointer">
                    {cityLabel === 'Ma position' ? 'moi' : cityLabel}
                  </button>
                </>
              )}
            </>
          )}
          {' '}?
        </h1>
      </div>

      {/* Bouton Filtrer */}
      <div className="px-5 mb-4">
        <button
          onClick={() => setIsFilterSheetOpen(true)}
          className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[13px] font-medium font-sans transition ${
            activeFilterCount > 0
              ? 'bg-terre text-paper-elev'
              : 'bg-paper-elev text-ink-1 ring-1 ring-paper-edge hover:bg-paper-rec'
          }`}
        >
          <SlidersHorizontal size={13} />
          {activeFilterCount > 0 ? `Filtres (${activeFilterCount})` : 'Filtrer'}
        </button>
      </div>

      {/* Category tiles — carrés avec emoji */}
      <div className="mb-7 overflow-x-auto scrollbar-none">
        <div className="flex gap-2 px-5 pt-1 pb-3">
          {/* Tout */}
          <button
            onClick={() => setKindFilters({})}
            className="flex flex-col items-center gap-1 w-[60px] shrink-0"
          >
            <span className={`w-12 h-12 rounded-xl flex items-center justify-center text-[22px] leading-none font-bold ${
              Object.keys(kindFilters).length === 0 ? 'bg-ink-1 text-paper' : 'bg-paper-elev text-ink-3'
            }`}>
              ✦
            </span>
            <span className={`text-[11px] font-medium leading-tight text-center ${
              Object.keys(kindFilters).length === 0 ? 'text-ink-1' : 'text-ink-2'
            }`}>Tout</span>
          </button>
          {/* Catégories */}
          {CATEGORY_LIST.map(c => {
            const isActive = kindFilters[c.id] === 'include';
            return (
              <button
                key={c.id}
                onClick={() => setKindFilters((current) => cycleEventKindFilter(current, c.id))}
                className="flex flex-col items-center gap-1 w-[60px] shrink-0"
              >
                <span className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                  isActive ? 'bg-ink-1' : 'bg-paper-elev'
                }`}>
                  <span className="grayscale">{c.emoji}</span>
                </span>
                <span className={`text-[11px] font-medium leading-tight text-center ${
                  isActive ? 'text-ink-1' : 'text-ink-2'
                }`}>{c.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Hero */}
      {fetchError ? (
        <div className="px-5 py-12 flex flex-col items-center gap-4 text-center">
          <span className="t-ed-md text-ink-2">Une erreur est survenue.</span>
          <span className="t-ui-sm text-ink-3">Réessayez plus tard.</span>
          <button
            onClick={() => { setBlockingLoading(true); setFetchError(false); setRetryKey(k => k + 1); }}
            className="mt-2 h-9 px-4 rounded-full bg-terre text-paper-elev t-ui-sm font-medium"
          >
            Réessayer
          </button>
        </div>
      ) : loading ? (
        <div className="px-5"><Skeleton className="aspect-square w-full" /></div>
      ) : hero ? (
        <section className="px-5 mb-10"><EventCard event={hero} variant="hero" onTap={() => setSelectedEventId(hero.id)} /></section>
      ) : (
        <EmptyState icon={<MagnifyingGlass size={28} />} title="Rien à l'horizon"
          body="Ajustez les filtres ou explorez d'autres jours."
          cta="Élargir les filtres" onCta={() => { setTime('week'); setKindFilters({}); }} />
      )}

      {/* Editorial filet between hero and grid */}
      {hero && grid.length > 0 && (
        <div className="px-5 mb-7 flex items-center gap-3">
          <GoldenRule />
          <span className="t-ed-md italic text-ink-2 whitespace-nowrap">À voir aussi</span>
          <GoldenRule />
        </div>
      )}

      {/* Magazine grid */}
      {grid.length > 0 && (
        <section className="px-5 grid grid-cols-2 gap-x-4 mb-12">
          {grid.map(e => <EventCard key={e.id} event={e} variant="grid" onTap={() => setSelectedEventId(e.id)} />)}
        </section>
      )}

      {/* Pub native — rupture éditoriale entre la grille et la liste longue */}
      {grid.length > 0 && numbered.length > 0 && (
        <section className="px-5 mb-12 max-w-[520px] mx-auto">
          <AdNativeCard variant="card" adKey="discover-primary" />
        </section>
      )}

      {/* Time Out style numbered list */}
      {numbered.length > 0 && (
        <section className="px-5 mb-12">
          <div className="flex items-center gap-3 mb-6">
            <GoldenRule />
            <h2 className="t-ed-xl whitespace-nowrap">Encore <em className="italic-keyword">{numbered.length}</em> idées</h2>
            <GoldenRule />
          </div>
          <ol className="divide-y divide-paper-edge">
            {interleaveAds(numbered, AD_FREQUENCY.discover_list_interval, AD_FREQUENCY.discover_list_first_at).map((item, idx) => {
              if (isAdMarker(item)) {
                return <AdNativeCard key={item.key} variant="compact" adKey={`discover-list-${idx}`} />;
              }
              const eventIndex = numbered.indexOf(item);
              return (
                <li key={item.id} className="py-2">
                  <button type="button" onClick={() => setSelectedEventId(item.id)} className="flex items-baseline gap-4 w-full text-left group">
                    <span className="t-mono-sm text-ink-3 w-8 shrink-0">{String(eventIndex + 6).padStart(2, '0')}</span>
                    <div className="flex-1 min-w-0">
                      <h3 className="t-ed-md leading-tight truncate group-hover:text-terre transition">{item.title}</h3>
                      <span className="t-ui-xs text-ink-3">{upperCity(item.city) ?? '—'}</span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ol>
        </section>
      )}

      {/* Tail — avec pub intercalée toutes les 4 cartes */}
      {tail.length > 0 && (
        <section className="px-5 grid grid-cols-1 gap-y-7 mb-12">
          {interleaveAds(tail, 4, 3).map((item, idx) =>
            isAdMarker(item)
              ? <AdNativeCard key={item.key} variant="card" adKey={`discover-tail-${idx}`} />
              : <EventCard key={item.id} event={item} variant="magazine" onTap={() => setSelectedEventId(item.id)} />
          )}
        </section>
      )}

      <section className="px-5 mb-12">
        <Button variant="secondary" full iconLeft={<MapPin size={18} />} onClick={() => location.assign('/explorer')}>Voir sur la carte</Button>
      </section>

      <footer className="px-5 pb-10 t-mono-xs text-ink-3 text-center">
        Source : DATAtourisme · OpenAgenda · Contributions locales
      </footer>

      <EventDetailSheet eventId={selectedEventId} onClose={() => setSelectedEventId(null)} />

      <EditorialDateRangePicker
        open={datePickerOpen}
        selectedFrom={selectedFrom}
        selectedTo={selectedTo}
        onSelect={(from, to) => {
          setSelectedFrom(from);
          setSelectedTo(to);
          setTime('range');
        }}
        onClose={() => setDatePickerOpen(false)}
        title="Choisir une période"
      />

      {/* Bottom sheet — Filtres */}
      <AnimatePresence>
        {isFilterSheetOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 z-40 bg-ink-1/40"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => { setIsFilterSheetOpen(false); setSheetView('filters'); }}
            />
            {/* Sheet */}
            <motion.div
              className="fixed bottom-0 left-0 right-0 z-50 bg-paper rounded-t-2xl shadow-sheet pb-[env(safe-area-inset-bottom)]"
              initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            >
              {/* Drag handle */}
              <div className="flex justify-center pt-3 pb-1">
                <div className="w-10 h-1 rounded-full bg-paper-edge" />
              </div>

              {sheetView === 'city' ? (
                /* Vue sélection de ville */
                <div className="px-5 pb-5">
                  <div className="flex items-center gap-3 mb-4">
                    <button
                      onClick={() => { setSheetView('filters'); setCityQ(''); setCityList([]); setCityError(null); }}
                      className="text-terre t-ui-sm font-medium"
                    >
                      ← Retour
                    </button>
                    <span className="t-cap">Choisir un lieu</span>
                  </div>
                  <div className="flex gap-2 mb-3">
                    <Input
                      placeholder="Ex. Saint-Malo"
                      value={cityQ}
                      onChange={e => setCityQ(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && searchCity()}
                    />
                    <Button onClick={searchCity} loading={cityBusy} iconLeft={<MagnifyingGlass size={16} />}>Chercher</Button>
                  </div>
                  <Button variant="secondary" full onClick={useCurrentPosition} loading={cityBusy} iconLeft={<Crosshair size={16} />}>
                    Ma position actuelle
                  </Button>
                  {cityError ? <p role="alert" className="mt-3 t-ui-xs text-terre">{cityError}</p> : null}
                  <ul className="mt-3 divide-y divide-paper-edge max-h-48 overflow-y-auto">
                    {cityList.map((c, i) => (
                      <li key={`${i}-${c.display_name}`}>
                        <button onClick={() => pickCity(c)} className="w-full text-left py-3">
                          <div className="t-ed-md">{c.display_name.split(',')[0]}</div>
                          <div className="t-ui-xs text-ink-3 truncate">{c.display_name}</div>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                /* Vue filtres principaux */
                <div className="px-5 pb-5">
                  {/* Section Quand */}
                  <h3 className="t-cap mb-3">Quand</h3>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {TIME_CHIPS.map(c => (
                      <Chip
                        key={c.id}
                        active={time === c.id}
                        onClick={() => {
                          setTime(c.id);
                          if (c.id === 'range') setDatePickerOpen(true);
                        }}
                      >
                        {c.id === 'range' ? (time === 'range' ? rangeChipLabel : 'Choisir une date') : c.label}
                      </Chip>
                    ))}
                  </div>

                  {/* Filet séparateur */}
                  <div className="filet mb-5" />

                  {/* Section Où */}
                  <h3 className="t-cap mb-3">Où</h3>
                  <div className="flex flex-wrap gap-2 mb-5">
                    <Chip
                      active={!!cityLabel}
                      iconLeft={<MapPin size={13} />}
                      onClick={() => { setCityQ(''); setCityList([]); setCityError(null); setSheetView('city'); }}
                    >
                      {cityLabel ?? 'Choisir un lieu'}
                    </Chip>
                  </div>

                  {/* Filet séparateur */}
                  <div className="filet mb-5" />

                  {/* Section Rayon */}
                  <h3 className="t-cap mb-3">Rayon</h3>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {DISTANCE_CHIPS.map(d => (
                      <button
                        key={d}
                        onClick={() => setRadiusKm(d)}
                        className={`t-mono-sm h-9 px-3.5 rounded-full transition ${
                          radiusKm === d
                            ? 'bg-ink-1 text-paper-elev'
                            : 'bg-paper-elev text-ink-1 ring-1 ring-paper-edge hover:bg-paper-rec'
                        }`}
                      >
                        {d} km
                      </button>
                    ))}
                    <button
                      onClick={() => setRadiusKm('all')}
                      className={`t-mono-sm h-9 px-3.5 rounded-full transition ${
                        radiusKm === 'all'
                          ? 'bg-ink-1 text-paper-elev'
                          : 'bg-paper-elev text-ink-1 ring-1 ring-paper-edge hover:bg-paper-rec'
                      }`}
                    >
                      Toute la France
                    </button>
                  </div>

                  {/* CTA */}
                  <Button variant="primary" full onClick={() => setIsFilterSheetOpen(false)}>
                    Voir les événements →
                  </Button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Date picker monté après le sheet pour apparaître par-dessus (même z-index, ordre DOM) */}
      <EditorialDateRangePicker
        open={datePickerOpen}
        selectedFrom={selectedFrom}
        selectedTo={selectedTo}
        onSelect={(from, to) => {
          setSelectedFrom(from);
          setSelectedTo(to);
          setTime('range');
        }}
        onClose={() => setDatePickerOpen(false)}
        title="Choisir une période"
      />
    </div>
  );
}
